import { createColumnHelper } from "@tanstack/react-table";
import { IconFileTypePdf } from "@tabler/icons-react";
import type { ColumnDef } from "../table";
import { displayCurrencyAmount } from "../../utils/display";
import { riskIndicatorClass } from "./helpers";
import { ReceivablesNotesCell } from "./ReceivablesNotesCell";
import {
  ReceivablesCustomerContactHint,
  ReceivablesDraftReadyIcon,
  ReceivablesEmailDisabledIcon,
  ReceivablesEscalationIcon,
  ReceivablesUnreadDot,
} from "./ReceivablesRowIndicators";
import { EmailActivityHeaderFilter } from "./EmailActivityHeaderFilter";
import type { EmailActivityFilter, InvoiceReceivablesRow } from "./types";

const columnHelper = createColumnHelper<InvoiceReceivablesRow>();

export const receivablesInvoiceSelectionColumn: ColumnDef<
  InvoiceReceivablesRow,
  unknown
> = {
  id: "select",
  header: ({ table }) => (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-slate-300"
      checked={table.getIsAllPageRowsSelected()}
      ref={(el) => {
        if (!el) return;
        el.indeterminate =
          table.getIsSomePageRowsSelected() &&
          !table.getIsAllPageRowsSelected();
      }}
      onChange={table.getToggleAllPageRowsSelectedHandler()}
      onClick={(event) => event.stopPropagation()}
      aria-label="Select all invoices on this page"
    />
  ),
  cell: ({ row }) => (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-slate-300"
      checked={row.getIsSelected()}
      onChange={row.getToggleSelectedHandler()}
      onClick={(event) => event.stopPropagation()}
      aria-label={`Select invoice ${row.original.externalId}`}
    />
  ),
  enableSorting: false,
  enableResizing: false,
  size: 36,
  minSize: 36,
  maxSize: 40,
  meta: {
    align: "center",
    stopRowClick: true,
    headerClassName: "!px-2",
    cellClassName: "!px-2",
  },
};

function ReceivablesInvoiceEscalationStatus({
  isEscalated,
  escalationPendingApproval,
  escalationStepLabel,
  daysUntilNextEscalation,
}: Pick<
  InvoiceReceivablesRow,
  | "isEscalated"
  | "escalationPendingApproval"
  | "escalationStepLabel"
  | "daysUntilNextEscalation"
>) {
  const escalationTitle = [
    escalationStepLabel
      ? `Escalated: step ${escalationStepLabel}`
      : "Invoice escalated",
    daysUntilNextEscalation != null
      ? `next escalation in ${daysUntilNextEscalation}d`
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex flex-wrap items-center justify-start gap-1">
      {isEscalated ? (
        <ReceivablesEscalationIcon show title={escalationTitle} />
      ) : null}
      {isEscalated ? (
        <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-800">
          {escalationStepLabel ? `Step ${escalationStepLabel}` : "Escalated"}
        </span>
      ) : null}
      {escalationPendingApproval ? (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
          Pending approval
        </span>
      ) : null}
      {daysUntilNextEscalation != null ? (
        <span className="text-xs text-slate-500">
          Next in {daysUntilNextEscalation}d
        </span>
      ) : null}
    </div>
  );
}

export const receivablesInvoiceEmailStatusColumn: ColumnDef<
  InvoiceReceivablesRow,
  unknown
> = columnHelper.display({
  id: "emailStatus",
  header: "",
  enableSorting: false,
  enableResizing: false,
  size: 120,
  meta: {
    align: "start",
  },
  cell: ({ row }) => {
    const { isEmailSendingEnabled, isEscalated, escalationPendingApproval } =
      row.original;

    if (isEscalated || escalationPendingApproval) {
      return <ReceivablesInvoiceEscalationStatus {...row.original} />;
    }

    return <ReceivablesEmailDisabledIcon disabled={!isEmailSendingEnabled} />;
  },
});

function parseDisplayDate(value: string): number {
  if (value === "-") return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
}

export function createReceivablesInvoicesColumns(options?: {
  onOpenCustomer?: (customerId: number) => void;
  /** Rendered after the identifying columns so it can't push them right. */
  emailStatusColumn?: ColumnDef<InvoiceReceivablesRow, unknown>;
  /** Drives the Email column's own header filter. */
  emailActivityFilter?: EmailActivityFilter;
  onEmailActivityFilterChange?: (value: EmailActivityFilter) => void;
}): ColumnDef<InvoiceReceivablesRow, unknown>[] {
  return [
    columnHelper.accessor("externalId", {
      header: "Invoice #",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 110,
      meta: {
        align: "start",
        cellClassName: "font-semibold text-blue-700",
      },
      cell: ({ row }) => <span>{row.original.externalId}</span>,
    }),
    columnHelper.display({
      id: "emailActivity",
      header: () =>
        options?.onEmailActivityFilterChange ? (
          <EmailActivityHeaderFilter
            value={options.emailActivityFilter ?? "all"}
            onChange={options.onEmailActivityFilterChange}
          />
        ) : (
          "Email"
        ),
      enableSorting: false,
      size: 78,
      meta: {
        align: "center",
        cellClassName: "text-center",
      },
      cell: ({ row }) => {
        const { hasDraft, hasUnreadCorrespondence } = row.original;
        if (!hasDraft && !hasUnreadCorrespondence) {
          return <span className="text-slate-300">—</span>;
        }
        return (
          <span className="flex items-center justify-center gap-1.5">
            <ReceivablesUnreadDot show={hasUnreadCorrespondence} />
            <ReceivablesDraftReadyIcon show={hasDraft} />
          </span>
        );
      },
    }),
    columnHelper.accessor("accountingSoftwareId", {
      header: "Client ID",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 220,
      minSize: 140,
      meta: {
        align: "start",
        cellClassName: "overflow-hidden text-slate-600 tabular-nums",
      },
      cell: ({ getValue }) => {
        const value = String(getValue() ?? "");
        return (
          <span className="block truncate" title={value}>
            {value}
          </span>
        );
      },
    }),
    columnHelper.accessor("customerName", {
      header: "Customer",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 180,
      meta: {
        align: "start",
        cellClassName: "overflow-hidden font-semibold text-blue-700",
      },
      cell: ({ row }) => {
        const { customerId, customerName, primaryEmail, primaryPhone } =
          row.original;
        const customerLabel = options?.onOpenCustomer ? (
          <button
            type="button"
            className="block w-full truncate text-left font-semibold text-blue-600 hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              options.onOpenCustomer?.(customerId);
            }}
          >
            {customerName}
          </button>
        ) : (
          <span className="block truncate">{customerName}</span>
        );

        return (
          <ReceivablesCustomerContactHint
            name={customerName}
            email={primaryEmail}
            phone={primaryPhone}
          >
            {customerLabel}
          </ReceivablesCustomerContactHint>
        );
      },
    }),
    ...(options?.emailStatusColumn ? [options.emailStatusColumn] : []),
    columnHelper.accessor("amount", {
      header: "Amount",
      enableSorting: true,
      size: 120,
      meta: {
        align: "end",
        cellClassName: "whitespace-nowrap tabular-nums",
      },
      cell: ({ row }) =>
        displayCurrencyAmount(row.original.amount, row.original.currency),
    }),
    columnHelper.accessor("dueDate", {
      header: "Due Date",
      enableSorting: true,
      sortingFn: (rowA, rowB) =>
        parseDisplayDate(rowA.original.dueDate) -
        parseDisplayDate(rowB.original.dueDate),
      size: 130,
      meta: {
        align: "start",
        cellClassName: "whitespace-nowrap",
      },
    }),
    columnHelper.accessor("overdueDays", {
      header: "Overdue",
      enableSorting: true,
      size: 120,
      meta: {
        align: "start",
        cellClassName: "whitespace-nowrap",
      },
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center gap-2 font-medium ${riskIndicatorClass(row.original.riskLevel)}`}
        >
          <span className="h-2 w-2 rounded-full bg-current" />
          {row.original.overdueDays > 0
            ? `${row.original.overdueDays} days`
            : "On time"}
        </span>
      ),
    }),
    columnHelper.accessor("remindedDate", {
      header: "Reminded",
      enableSorting: true,
      sortingFn: (rowA, rowB) =>
        parseDisplayDate(rowA.original.remindedDate) -
        parseDisplayDate(rowB.original.remindedDate),
      size: 130,
      meta: {
        align: "start",
        cellClassName: "whitespace-nowrap",
      },
    }),
    columnHelper.accessor("notes", {
      header: "Notes",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 180,
      meta: {
        align: "start",
        cellClassName: "truncate",
      },
      cell: ({ row }) => <ReceivablesNotesCell notes={row.original.notes} />,
    }),
  ];
}

export function createReceivablesInvoiceActionsColumn(options: {
  onViewFile: (externalId: string) => void;
}): ColumnDef<InvoiceReceivablesRow, unknown> {
  return columnHelper.display({
    id: "actions",
    header: "",
    enableSorting: false,
    enableResizing: false,
    size: 48,
    meta: {
      align: "center",
    },
    cell: ({ row }) => (
      <button
        type="button"
        title="View invoice file"
        aria-label={`View file for invoice ${row.original.externalId}`}
        onClick={(event) => {
          event.stopPropagation();
          options.onViewFile(row.original.externalId);
        }}
        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-[#EEF4F2] hover:text-[#4B7F73]"
      >
        <IconFileTypePdf size={18} stroke={1.75} />
      </button>
    ),
  });
}
